<script setup lang="ts">
import { computed, ref } from "vue";

import DocumentViewerFile from "./DocumentViewer/DocumentViewerFile.vue";
import VoiceTranscriptFile from "./VoiceTranscript/VoiceTranscriptFile.vue";
import FileRedactionMessage from "@/components/FileRedactionMessage.vue";

import { useMetadataStore } from "@/stores/metadata-store";
import { useAnnotationStore } from "@/stores/annotation-store";

import { isItemBurning, isThisMe } from "@/services/UtilitiesService";

import type { HighlightCustomArea } from "@/models/Viewer";
import type { DockviewPanelComponentEmits, DockviewPanelComponentProps } from "@/hooks/use-case-panel";

withDefaults(defineProps<DockviewPanelComponentProps<true>>(), {  })
defineEmits<DockviewPanelComponentEmits>();

const selectedArea = ref<HighlightCustomArea>();

const routeParams = computed(() => __props.dockview?.params.popoutRouteParams);

const currentFile = computed(() => {
  if (routeParams.value?.casefile == null) return;

  const file = useMetadataStore().files.find((f) => isThisMe(f, routeParams.value!.casefile));
  if (file == null) return;

  return {
    file,
    type: file.fileType === "audio" || file.fileType === "ac5" ? ("transcript" as const) : ("document" as const),
  };
});

// It is used
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getProps() {
  if (__props.dockview == null) return;

  return {
    isPopout: true,
    isVisible: true,
    scale: __props.dockview.size,
    routeParams: routeParams.value,
  };
}

useAnnotationStore().$onAction(({ name, args }) => {
  if (name !== ("selectArea" satisfies typeof name) || args[1] !== routeParams.value?.casefile) return;
  selectedArea.value = args[0];
});
</script>
<template>
  <template v-if="dockview != null && routeParams != null && currentFile != null">
    <FileRedactionMessage
      v-if="isItemBurning(currentFile.file)"
      class="tw-absolute tw-left-0 tw-top-0 tw-flex tw-size-full tw-items-center tw-justify-center tw-p-4"
      :file="currentFile.file"
      :route-params="routeParams"
    />
    <VoiceTranscriptFile
      v-else-if="currentFile.type === 'transcript'"
      is-popout
      is-visible
      :scale="dockview.size"
      :file="currentFile.file"
      :route-params="routeParams"
      @panel-popout-action="(arg) => $emit('panelPopoutAction', arg)"
    />
    <DocumentViewerFile
      v-else
      is-popout
      is-visible
      :scale="dockview.size"
      :file="currentFile.file"
      :route-params="routeParams"
      :selected-area="selectedArea"
      @panel-popout-action="(arg) => $emit('panelPopoutAction', arg)"
    />
  </template>
</template>
